import { type components } from '@/generated/internal-api/openapi-types';
import { type ISODateTime } from '@/model/Common.model.ts';
import type { FilterOption } from '@/components/UI/Table/TableComponent.model.ts';
import { FilterTypes as filterType } from '@/constants/FilterTypes.ts';

export type BenchlingBackfillApiId = components['schemas']['BenchlingBackfillApiId.schema'];
export type BenchlingBackfillEventApiId = components['schemas']['BenchlingBackfillEventApiId.schema'];
export type BenchlingBackfillEntityType = components['schemas']['BenchlingBackfillEntityType.schema'];
export type BenchlingBackfillEventStatus = components['schemas']['BenchlingBackfillEventStatus.schema'];
export type BenchlingErrorType = components['schemas']['BenchlingErrorType.schema'];

export type BenchlingEntityType = components['schemas']['BenchlingBackfillEntityType.schema'];

export type BenchlingSyncEventApiId = components['schemas']['BenchlingSyncEventApiId.schema'];
export type SyncEntityDetailsV1 = components['schemas']['SyncEntityDetailsV1.schema'];
export type SyncEntityStudyDetailsV1 = components['schemas']['SyncEntityStudyDetailsV1.schema'];
export type SyncEntityAnimalDetailsV1 = components['schemas']['SyncEntityAnimalDetailsV1.schema'];
export type SyncEntityStudyGroupDetailsV1 = components['schemas']['SyncEntityStudyGroupDetailsV1.schema'];
export type SyncEntityTreatmentDetailsV1 = components['schemas']['SyncEntityTreatmentDetailsV1.schema'];
export type SyncEntityMeasurementDetailsV1 = components['schemas']['SyncEntityMeasurementDetailsV1.schema'];
export type SyncEntityDosageDetailsV1 = components['schemas']['SyncEntityDosageDetailsV1.schema'];
export type SyncEntitySampleDetailsV1 = components['schemas']['SyncEntitySampleDetailsV1.schema'];
export type SyncEntityObservationDetailsV1 = components['schemas']['SyncEntityObservationDetailsV1.schema'];

export interface BackfillEvent {
  api_id: BenchlingBackfillEventApiId;
  backfill_api_id: BenchlingBackfillApiId;
  entity_api_id: string;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  study: components['schemas']['StudyInfoV1.schema'];
  error?: string;
  error_type?: components['schemas']['BenchlingErrorType.schema'];
  acknowledged_by?: { id: string; name: string } | null;
  acknowledged_at?: ISODateTime;
  event: string;
  entity_type: components['schemas']['BenchlingBackfillEntityType.schema'];
  entity_id: string;
  status: components['schemas']['BenchlingBackfillEventStatus.schema'];
}

export const entityTypeToStringMap: Record<BenchlingBackfillEntityType, string> = {
  animals: 'Animal',
  dosages: 'Dosage',
  measurements: 'Measurement',
  observations: 'Observation',
  samples: 'Sample',
  studies: 'Study',
  study_groups: 'Study group',
  treatments: 'Treatment',
} as const;

export const entityTypeToLinkStringMap: Record<BenchlingBackfillEntityType, string> = {
  animals: 'Go to animal profile',
  dosages: 'Go to animal dosages',
  measurements: 'Go to animal profile',
  observations: 'Go to animal observations',
  samples: 'Go to animal samples',
  studies: 'Go to study',
  study_groups: 'Go to study groups',
  treatments: 'Go to study treatments',
} as const;

export const statusToStringMap: Record<BenchlingBackfillEventStatus, string> = {
  queued: 'In progress',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  deactivated: 'Deactivated',
  resolved: 'Resolved',
  acknowledged: 'Acknowledged',
} as const;

export const errorTypeToStringMap: Record<BenchlingErrorType, string> = {
  app_config_error: 'App misconfiguration',
  duplicate_entity: 'Duplicate entity',
  dropdown_value_not_found: 'Dropdown value not found',
  entity_already_archived: 'Entity already archived',
  entity_not_found: 'Entity not found',
  entity_not_registered: 'Entity not registered',
  field_not_found: 'Field not found',
  incompatible_field_type: 'Incompatible field type',
  insufficient_permissions: 'Insufficient permissions',
  invalid_field: 'Invalid field',
  item_not_found: 'Item not found',
  label_collision: 'Label collision',
  maintenance_error: 'Maintenance error',
  missing_value: 'Missing value',
  non_unique_naming: 'Non-unique naming',
  parent_not_found: 'Parent not found',
  rate_limit_error: 'Rate limit error',
  task_timeout: 'Task timeout',
  batch_failed: 'Batch failed',
  unhandled_benchling_error: 'Unhandled Benchling error',
  unhandled_error: 'Unhandled error',
} as const;

const statusToFilterOptions = (): { value: string; name: string }[] => {
  return Object.entries(statusToStringMap).map(([key, value]) => ({
    value: key,
    name: value,
  }));
};

const entityTypeToFilterOptions = (): { value: string; name: string }[] => {
  return Object.entries(entityTypeToStringMap).map(([key, value]) => ({
    value: key,
    name: value,
  }));
};

const errorTypeToFilterOptions = (): { value: string; name: string }[] => {
  return Object.entries(errorTypeToStringMap).map(([key, value]) => ({
    value: key,
    name: value,
  }));
};

export const eventFilterOptions: Array<FilterOption> = [
  {
    value: 'backfill_error_type',
    name: 'Error type',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: errorTypeToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: errorTypeToFilterOptions(),
      },
    ],
  },
  {
    value: 'backfill_entity_type',
    name: 'Item type',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: entityTypeToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: entityTypeToFilterOptions(),
      },
    ],
  },
  {
    value: 'backfill_status',
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: statusToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: statusToFilterOptions(),
      },
    ],
  },
  {
    value: 'backfill_study',
    name: 'Study',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: [],
      },
    ],
    fetchFromApi: {
      placeholder: 'Search studies',
      url: 'studies.base',
    },
  },
  {
    value: 'backfill_updated_at',
    name: 'Updated at',
    operations: [
      {
        name: filterType.between,
        value: filterType.between,
      },
    ],
  },
] as const;

export const syncEventFilterOptions: Array<FilterOption> = [
  {
    value: 'sync_error_type',
    name: 'Error type',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: errorTypeToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: errorTypeToFilterOptions(),
      },
    ],
  },
  {
    value: 'sync_entity_type',
    name: 'Item type',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: entityTypeToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: entityTypeToFilterOptions(),
      },
    ],
  },
  {
    value: 'sync_status',
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: statusToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: statusToFilterOptions(),
      },
    ],
  },
  {
    value: 'sync_study',
    name: 'Study',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: [],
      },
    ],
    fetchFromApi: {
      placeholder: 'Search studies',
      url: 'studies.base',
    },
  },
  {
    value: 'sync_updated_at',
    name: 'Updated at',
    operations: [
      {
        name: filterType.between,
        value: filterType.between,
      },
    ],
  },
] as const;

/**
 * Custom filter processor for BenchlingBackfill events.
 * Automatically adds a null exclusion filter when using date range filtering on updated_at.
 */
export const benchlingBackfillCustomFilterProcessor = (filters: Array<any>, params: URLSearchParams): void => {
  const hasUpdatedAtFilter = filters.some(
    (filter) => filter.category?.value === 'backfill_updated_at' && filter.operation?.name === filterType.between
  );

  if (hasUpdatedAtFilter) {
    params.append('filters[]', 'backfill_updated_at|ne|null');
  }
};

/**
 * Custom filter processor for BenchlingSyncEvent events.
 * Automatically adds a null exclusion filter when using date range filtering on updated_at.
 */
export const benchlingSyncEventCustomFilterProcessor = (filters: Array<any>, params: URLSearchParams): void => {
  const hasUpdatedAtFilter = filters.some(
    (filter) => filter.category?.value === 'sync_updated_at' && filter.operation?.name === filterType.between
  );

  if (hasUpdatedAtFilter) {
    params.append('filters[]', 'sync_updated_at|ne|null');
  }
};

export type BenchlingBackfillV1 = components['schemas']['BenchlingBackfillV1.schema'];

/**
 * Helper function to get status filter configuration from filter options array.
 * Dynamically finds the status filter by field name and returns both value and index.
 */
export const getStatusFilterConfig = (
  filterOptions: Array<FilterOption>,
  statusFieldName: string
): { categoryValue: string; categoryIndex: number } => {
  const statusFilterIndex = filterOptions.findIndex((option) => option.value === statusFieldName);

  return {
    categoryValue: statusFieldName,
    categoryIndex: statusFilterIndex,
  };
};

// Pre-computed filter configs to avoid re-calculation on every render
export const BACKFILL_STATUS_FILTER_CONFIG = getStatusFilterConfig(eventFilterOptions, 'backfill_status');
export const SYNC_STATUS_FILTER_CONFIG = getStatusFilterConfig(syncEventFilterOptions, 'sync_status');
